import React, { Fragment, useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import {
  Breadcrumbs,
  Grid,
  Card,
  CardContent,
  Typography,
  Hidden,
  Button,
  ButtonGroup,
} from "@material-ui/core";
import { withStyles, makeStyles, createStyles } from "@material-ui/core/styles";
import HomeStyles from "pages/HomeStyles";
import { setCurrentPage, getByAreaData, getByAreaTrendData } from "actions";
import * as C from "components";
import ByAreaSummary from "components/PicTree/Game/Area/ByAreaSummary";
import ByAreaDetailTable from "components/PicTree/Game/Area/ByAreaDetailTable";
import ByAreaMap from "components/PicTree/Game/Area/ByAreaMap";
import PicTreeByAreaTrendChart from "components/Charts/PicTreeByAreaTrendChart";
import CalendarRangePicker from "components/Calendar/CalendarRangePicker";
import CalendarRangePicTreeNtt from "components/Calendar/CalendarRangePicTreeNtt";
import CalendarRangePicTreeNttFt from "components/Calendar/CalendarRangePicTreeNttFt";
import CalendarRangePicTreeHokuden from "components/Calendar/CalendarRangePicTreeHokuden";
import { useLocation } from "react-router-dom";
import { isRegularSeasonId } from "../../../../constants/pictreeRegularSeasonIds";

const useStyles = makeStyles((theme) =>
  createStyles({
    loaderBox: {
      width: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      minHeight: 280,
    },
    loaderBarImg: {
      width: 40,
    },
    breadcrumbs: {
      marginTop: 16,
      minHeight: 30,
    },
    gridSection: {
      marginTop: theme.spacing(1),
    },
    selectGameTxt: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      minHeight: 280,
      flexWrap: "wrap",
      "& > *": {
        margin: theme.spacing(0.5),
      },
    },
    tableModeBar: {
      display: "flex",
      justifyContent: "flex-end",
      alignItems: "center",
      marginBottom: theme.spacing(1),
    },
    trendSortLabel: {
      color: theme.palette.text.primary,
      fontSize: 12,
      lineHeight: 1,
      marginRight: theme.spacing(1),
      whiteSpace: "nowrap",
    },
    modeButton: {
      minWidth: 72,
    },
  })
);

const PicTreePhotoByArea = ({
  currentPage,
  setCurrentPage,
  pageTopLoader,
  dateRangePicTree,
  dateRangePicTreeNtt,
  dateRangePicTreeNttFt,
  dateRangePicTreeHokuden,
  selectedSeasonTepco,
  getByAreaData,
  byAreaDataSummaryTepco,
  byAreaDataTableTepco,
  byAreaDataSummaryNtt,
  byAreaDataTableNtt,
  byAreaDataRowsNtt,
  byAreaDataSummaryNttFt,
  byAreaDataTableNttFt,
  byAreaDataSummaryHokuden,
  byAreaDataTableHokuden,
  getByAreaTrendData,
  byAreaTrendDataNtt,
  byAreaTrendLoadingNtt,
  selectedSeasonNtt,
  selectedSeasonHokuden,
  selectedAreaLayerId,
}) => {
  const classes = useStyles();
  const location = useLocation();
  const [company, setCompany] = useState(null);
  const [summaryData, setSummaryData] = useState(null);
  const [tableData, setTableData] = useState(null);
  const [mapRows, setMapRows] = useState(null);
  const [companyLbl, setCompanyLbl] = useState(null);
  const [viewMode, setViewMode] = useState("table");
  const [tableSearchText, setTableSearchText] = useState("");
  const [tableRowsPerPage, setTableRowsPerPage] = useState(200);
  const [trendSortDirection, setTrendSortDirection] = useState("DESC");
  const prevViewModeRef = useRef(viewMode);
  const lastTableFetchKeyRef = useRef("");
  const lastTepcoAutoFetchKeyRef = useRef("");
  const lastHokudenAutoFetchKeyRef = useRef("");
  const isNtt = company === "ntt";
  const isRegularSeasonSelected =
    Array.isArray(selectedSeasonNtt) &&
    selectedSeasonNtt.some((season) => isRegularSeasonId(season?.seasonId));
  const isMapAllowed =
    isNtt &&
    isRegularSeasonSelected &&
    ["regular_1_l1", "regular_1_l2", "regular_1_l3"].includes(selectedAreaLayerId);
  const isTrendAllowed =
    isNtt &&
    isRegularSeasonSelected &&
    ["regular_1_l1", "regular_1_l2", "regular_1_l3"].includes(selectedAreaLayerId);
  const isTableQueryAllowed = isNtt && isRegularSeasonSelected;

  useEffect(() => {
    setCurrentPage(location.pathname);
    const seg = location.pathname.split("/");
    const nextCompany = seg[3] || null;
    setCompany(nextCompany);
    setSummaryData(null);
    setTableData(null);
    setMapRows(null);
    setTableSearchText("");
    setTableRowsPerPage(200);
    setTrendSortDirection("DESC");

    if (nextCompany === "ntt") {
      setCompanyLbl("NTT ME");
    } else if (nextCompany === "ntt-ft") {
      setCompanyLbl("NTT FT");
    } else {
      setCompanyLbl(nextCompany ? nextCompany.toUpperCase() : null);
    }
    setViewMode("table");
    lastTepcoAutoFetchKeyRef.current = "";
    lastHokudenAutoFetchKeyRef.current = "";
    if (nextCompany !== "ntt" && nextCompany !== "tepco" && nextCompany !== "hokuden") {
      getByAreaData(nextCompany);
    }
  }, [location.pathname, setCurrentPage, getByAreaData]);

  useEffect(() => {
    if (company !== "tepco") {
      return;
    }

    if (!Array.isArray(dateRangePicTree) || dateRangePicTree.length < 2) {
      return;
    }

    if (!Array.isArray(selectedSeasonTepco) || selectedSeasonTepco.length === 0) {
      return;
    }

    const nextAutoFetchKey = [
      company,
      selectedSeasonTepco.map((season) => season?.seasonId || "").join(","),
      dateRangePicTree.join("|"),
    ].join("::");

    if (lastTepcoAutoFetchKeyRef.current === nextAutoFetchKey) {
      return;
    }

    lastTepcoAutoFetchKeyRef.current = nextAutoFetchKey;
    getByAreaData("tepco");
  }, [company, dateRangePicTree, selectedSeasonTepco, getByAreaData]);

  useEffect(() => {
    if (company !== "hokuden") {
      return;
    }

    if (
      !Array.isArray(dateRangePicTreeHokuden) ||
      dateRangePicTreeHokuden.length < 2 ||
      !dateRangePicTreeHokuden[0] ||
      !dateRangePicTreeHokuden[1]
    ) {
      return;
    }

    if (
      !Array.isArray(selectedSeasonHokuden) ||
      selectedSeasonHokuden.length === 0
    ) {
      return;
    }

    const nextAutoFetchKey = [
      company,
      selectedSeasonHokuden.map((season) => season?.seasonId || "").join(","),
      dateRangePicTreeHokuden.join("|"),
    ].join("::");

    if (lastHokudenAutoFetchKeyRef.current === nextAutoFetchKey) {
      return;
    }

    lastHokudenAutoFetchKeyRef.current = nextAutoFetchKey;
    getByAreaData("hokuden");
  }, [
    company,
    dateRangePicTreeHokuden,
    selectedSeasonHokuden,
    getByAreaData,
  ]);

  useEffect(() => {
    if (!isMapAllowed && viewMode === "map") {
      setViewMode("table");
    }
  }, [isMapAllowed, viewMode]);

  useEffect(() => {
    if (!isTrendAllowed && viewMode === "trend") {
      setViewMode("table");
    }
  }, [isTrendAllowed, viewMode]);

  useEffect(() => {
    if (viewMode === "trend" && isTrendAllowed) {
      getByAreaTrendData({
        company: "ntt",
        sortDirection: trendSortDirection,
      });
    }
  }, [
    viewMode,
    isTrendAllowed,
    selectedAreaLayerId,
    dateRangePicTreeNtt,
    selectedSeasonNtt,
    trendSortDirection,
    getByAreaTrendData,
  ]);

  useEffect(() => {
    if (company !== "ntt" || viewMode !== "trend" || !isTrendAllowed) {
      return;
    }

    getByAreaData({
      company: "ntt",
      mode: "summary",
    });
  }, [
    company,
    viewMode,
    isTrendAllowed,
    selectedAreaLayerId,
    dateRangePicTreeNtt,
    selectedSeasonNtt,
    getByAreaData,
  ]);

  useEffect(() => {
    if (company !== "ntt" || viewMode !== "map" || !isMapAllowed) {
      return;
    }

    getByAreaData({
      company: "ntt",
      mode: "map",
    });
  }, [
    company,
    viewMode,
    isMapAllowed,
    selectedAreaLayerId,
    dateRangePicTreeNtt,
    selectedSeasonNtt,
    getByAreaData,
  ]);

  useEffect(() => {
    const nextFetchKey = [
      company || "",
      selectedAreaLayerId || "",
      Array.isArray(dateRangePicTreeNtt) ? dateRangePicTreeNtt.join("|") : "",
      Array.isArray(selectedSeasonNtt)
        ? selectedSeasonNtt.map((season) => season?.seasonId || "").join(",")
        : "",
      viewMode === "table" ? tableSearchText : "",
      viewMode === "table" ? String(tableRowsPerPage || "") : "",
    ].join("::");

    const shouldFetchTableData =
      isTableQueryAllowed &&
      viewMode === "table" &&
      (
        prevViewModeRef.current !== "table" ||
        lastTableFetchKeyRef.current !== nextFetchKey
      );

    if (shouldFetchTableData) {
      getByAreaData({
        company: "ntt",
        mode: "table",
        searchText: tableSearchText,
        rowsPerPage: tableRowsPerPage,
      });
      lastTableFetchKeyRef.current = nextFetchKey;
    }

    prevViewModeRef.current = viewMode;
  }, [
    company,
    viewMode,
    selectedAreaLayerId,
    dateRangePicTreeNtt,
    selectedSeasonNtt,
    getByAreaData,
    tableSearchText,
    tableRowsPerPage,
    isTableQueryAllowed,
  ]);

  useEffect(() => {
    if (company === "tepco") {
      setSummaryData(byAreaDataSummaryTepco);
      setTableData(byAreaDataTableTepco);
      setMapRows(null);
    } else if (company === "ntt") {
      setSummaryData(byAreaDataSummaryNtt);
      const expectedColumnCount =
        ["regular_1_l2", "regular_1_l3"].includes(selectedAreaLayerId)
          ? 8
          : 7;
      const nextTableData = Array.isArray(byAreaDataTableNtt)
        ? byAreaDataTableNtt
        : [];
      const hasExpectedShape =
        nextTableData.length === 0 ||
        (Array.isArray(nextTableData[0]) &&
          nextTableData[0].length === expectedColumnCount);

      setTableData(hasExpectedShape ? nextTableData : []);
      setMapRows(byAreaDataRowsNtt);
    } else if (company === "ntt-ft") {
      setSummaryData(byAreaDataSummaryNttFt);
      setTableData(byAreaDataTableNttFt);
      setMapRows(null);
    } else if (company === "hokuden") {
      setSummaryData(byAreaDataSummaryHokuden);
      setTableData(byAreaDataTableHokuden);
      setMapRows(null);
    }
  }, [
    company,
    viewMode,
    byAreaDataSummaryTepco,
    byAreaDataTableTepco,
    byAreaDataSummaryNtt,
    byAreaDataTableNtt,
    byAreaDataRowsNtt,
    byAreaDataSummaryNttFt,
    byAreaDataTableNttFt,
    byAreaDataSummaryHokuden,
    byAreaDataTableHokuden,
    selectedAreaLayerId,
  ]);

  const handleChangeViewMode = (mode) => {
    if (mode === "map" && !isMapAllowed) {
      return;
    }
    if (mode === "trend" && !isTrendAllowed) {
      return;
    }
    setViewMode(mode);
  };

  const handleChangeTrendSortDirection = (direction) => {
    setTrendSortDirection(direction);
  };

  return (
    <Fragment>
      <C.ContentWrapper>
        <Grid
          container
          spacing={0}
          direction="row"
          style={{ marginTop: -16, marginBottom: 8 }}
          justifyContent="space-between"
        >
          <Grid item>
            <Breadcrumbs separator="›" className={classes.breadcrumbs}>
              <Typography>PicTrée {companyLbl}</Typography>
              <Typography variant="body1">撮影</Typography>
              <Typography variant="body1">エリア別</Typography>
            </Breadcrumbs>
          </Grid>
          <Grid item className={classes.fillRemailSpace}></Grid>
          <Grid item>
            {company && (
              <>
                {company === "tepco" && (
                  <CalendarRangePicker mDate={"2024/04/13"} page="by-area" pictree />
                )}
                {company === "ntt" && (
                  <CalendarRangePicTreeNtt mDate={"2024/04/13"} page="pictree-ntt" pictree />
                )}
                {company === "ntt-ft" && (
                  <CalendarRangePicTreeNttFt mDate={"2024/04/13"} page="pictree-ntt" pictree />
                )}
                {company === "hokuden" && (
                  <CalendarRangePicTreeHokuden mDate={"2024/04/13"} page="pictree-hokuden" pictree />
                )}
              </>
            )}
          </Grid>
        </Grid>

        <Hidden smDown>
          <Grid
            container
            justifyContent="center"
            spacing={0}
            style={{ marginTop: 0 }}
          >
            <Grid item xs={12} md={12} lg={10}>
              <Card>
                <CardContent>
                  <div className={classes.cardContentSection}>
                    <ByAreaSummary data={summaryData} />
                  </div>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Hidden>

        <Grid
          container
          spacing={1}
          direction="row"
          style={{ marginTop: 16, marginRight: 16, marginBottom: 8 }}
          justifyContent="center"
        >
          <Grid
            item
            xs={12}
            md={12}
            lg={10}
            style={{ marginTop: 0, justifyContent: "center" }}
          >
            {isNtt && (
              <div className={classes.tableModeBar}>
                {viewMode === "trend" ? (
                  <>
                    <Typography component="span" className={classes.trendSortLabel}>
                      達成率
                    </Typography>
                    <ButtonGroup size="small" variant="outlined" color="primary" style={{ marginRight: 8 }}>
                    <Button
                      className={classes.modeButton}
                      onClick={() => handleChangeTrendSortDirection("DESC")}
                      variant={trendSortDirection === "DESC" ? "contained" : "outlined"}
                      color={trendSortDirection === "DESC" ? "primary" : "default"}
                    >
                      高い順
                    </Button>
                    <Button
                      className={classes.modeButton}
                      onClick={() => handleChangeTrendSortDirection("ASC")}
                      variant={trendSortDirection === "ASC" ? "contained" : "outlined"}
                      color={trendSortDirection === "ASC" ? "primary" : "default"}
                    >
                      低い順
                    </Button>
                    </ButtonGroup>
                  </>
                ) : null}
                <ButtonGroup size="small" variant="outlined" color="primary">
                  <Button
                    className={classes.modeButton}
                    onClick={() => handleChangeViewMode("table")}
                    variant={viewMode === "table" ? "contained" : "outlined"}
                    color={viewMode === "table" ? "primary" : "default"}
                  >
                    一覧表示
                  </Button>
                  <Button
                    className={classes.modeButton}
                    onClick={() => handleChangeViewMode("map")}
                    variant={viewMode === "map" ? "contained" : "outlined"}
                    color={viewMode === "map" ? "primary" : "default"}
                    disabled={!isMapAllowed}
                  >
                    地図表示
                  </Button>
                  <Button
                    className={classes.modeButton}
                    onClick={() => handleChangeViewMode("trend")}
                    variant={viewMode === "trend" ? "contained" : "outlined"}
                    color={viewMode === "trend" ? "primary" : "default"}
                    disabled={!isTrendAllowed}
                  >
                    撮影進捗
                  </Button>
                </ButtonGroup>
              </div>
            )}
            {viewMode === "map" && isMapAllowed ? (
              <ByAreaMap data={mapRows} />
            ) : viewMode === "trend" && isTrendAllowed ? (
              <PicTreeByAreaTrendChart
                title="撮影進捗"
                subtitle={`${selectedAreaLayerId === "regular_1_l1"
                  ? "都道府県別"
                  : selectedAreaLayerId === "regular_1_l2"
                    ? "医療圏別"
                    : "市区町村別"
                }・日別推移`}
                data={byAreaTrendDataNtt}
                loading={byAreaTrendLoadingNtt}
                sortDirection={trendSortDirection}
              />
            ) : (
              <ByAreaDetailTable
                data={tableData}
                selectedAreaLayerId={selectedAreaLayerId}
                rowsPerPage={tableRowsPerPage}
                onSearchChange={setTableSearchText}
                onRowsPerPageChange={setTableRowsPerPage}
                key={`by-area-table-${selectedAreaLayerId || "none"}`}
              />
            )}
          </Grid>
        </Grid>
      </C.ContentWrapper>
    </Fragment>
  );
};

PicTreePhotoByArea.propTypes = {
  classes: PropTypes.object,
};

const mapStateToProps = (state) => ({
  pageTopLoader: state.page.pageTopLoader,
  dateRangePicTree: state.page.dateRangePicTree,
  dateRangePicTreeNtt: state.page.dateRangePicTreeNtt,
  dateRangePicTreeNttFt: state.page.dateRangePicTreeNttFt,
  dateRangePicTreeHokuden: state.page.dateRangePicTreeHokuden,
  byAreaDataSummaryTepco: state.pictree.byAreaDataSummary,
  byAreaDataTableTepco: state.pictree.byAreaDataTable,
  byAreaDataSummaryNtt: state.pictreeNtt.byAreaDataSummary,
  byAreaDataTableNtt: state.pictreeNtt.byAreaDataTable,
  byAreaDataRowsNtt: state.pictreeNtt.byAreaDataRows,
  byAreaDataSummaryNttFt: state.pictreeNttFt.byAreaDataSummary,
  byAreaDataTableNttFt: state.pictreeNttFt.byAreaDataTable,
  byAreaDataSummaryHokuden: state.pictreeHokuden.byAreaDataSummary,
  byAreaDataTableHokuden: state.pictreeHokuden.byAreaDataTable,
  byAreaTrendDataNtt: state.pictreeNtt.byAreaTrendData,
  byAreaTrendLoadingNtt: state.pictreeNtt.byAreaTrendLoading,
  selectedSeasonNtt: state.pictreeNtt.selectedSeason,
  selectedSeasonTepco: state.pictree.selectedSeason,
  selectedSeasonHokuden: state.pictreeHokuden.selectedSeason,
  selectedAreaLayerId: state.pictreeNtt.selectedAreaLayerId,
});

const mapDispatchToProps = (dispatch) => ({
  setCurrentPage: (param) => dispatch(setCurrentPage(param)),
  getByAreaData: (param) => dispatch(getByAreaData(param)),
  getByAreaTrendData: (param) => dispatch(getByAreaTrendData(param)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withStyles(HomeStyles, { withTheme: true })(PicTreePhotoByArea));
